import React from 'react';

import { FolderPickerDialog, type FolderPickerDialogProps } from '@lumapps/file-pickers/components/FolderPickerDialog';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import type { DocumentV1, MoveDocumentNavigationConfirm } from '@lumapps/medias-document/types';
import { useSelector } from '@lumapps/redux/react';
import { isLoadingStrict } from '@lumapps/utils/types/BaseLoadingStatus';

import { useMoveDocument } from '../../hooks/useMoveDocument';

export interface MoveDocumentPickerDialogProps extends Pick<FolderPickerDialogProps, 'isOpen'> {
    /** The document to be moved. */
    document: DocumentV1;
    /** Callback function to close the dialog. */
    onClose: () => void;
}

/**
 * A dialog component that allows users to move a document to a different folder.
 *
 * @param {MoveDocumentPickerDialogProps} props The properties for the component.
 * @returns {React.ReactElement} The `MoveDocumentPickerDialog` component.
 */
export const MoveDocumentPickerDialog: React.FC<MoveDocumentPickerDialogProps> = ({ document, isOpen, onClose }) => {
    const currentSiteId = useSelector(instanceIdSelector);

    const { mutateAsync: moveDocument, baseLoadingStatus: status } = useMoveDocument(document);

    /**
     * Handles the confirmation of the folder selection.
     *
     * @param {MoveDocumentNavigationConfirm} data
     */
    const onMoveDocumentHandler = React.useCallback(
        (data: MoveDocumentNavigationConfirm) => {
            // If a folder is selected, use its path. Otherwise, use the root path of the current site.
            const destinationPath =
                data.status === 'loaded' ? data.folder.docPath : `provider=local/site=${currentSiteId}`;

            moveDocument({ destinationPath, sourcePath: document.docPath }).then(onClose);
        },
        [currentSiteId, document.docPath, moveDocument, onClose],
    );

    return (
        <FolderPickerDialog
            initialDocument={document}
            isDialogLoading={isLoadingStrict(status)}
            isOpen={isOpen}
            onCancel={onClose}
            onConfirm={onMoveDocumentHandler}
        />
    );
};
